import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import { Button, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { compact } from 'lodash';
import React from 'react';

import { useConfirmation } from 'components/modals/ConfirmationContext';
import { Job, JobArchiveOperation, JobStatus } from 'interfaces/job';
import { Permission } from 'interfaces/permissionOption';
import { useAppSelector } from 'redux/hooks';
import { useJobArchiveMutation, usePendingJobArchiveOperation } from 'utils/queryHooks/jobs/useJobArchive';
import { usePermissions } from 'utils/usePermissions';

interface JobArchiveControlProps {
  job: Pick<Job, 'name' | 'status' | 'userId' | 'deletedAt'> & { id: string };
  labeled?: boolean;
  /** Dense timeline rows use the shared contained secondary archive treatment. */
  contained?: boolean;
  /** Called once an archive request succeeded, e.g. to close the job details. */
  onArchived?: () => void;
  /** Extra consequences to state in the confirmation, per operation. */
  confirmationNotes?: Partial<Record<JobArchiveOperation, string>>;
  /** Replaces the default action label when the surrounding screen has a more specific consequence. */
  actionLabels?: Partial<Record<JobArchiveOperation, string>>;
  /** Replaces the default confirmation body when the surrounding screen must state a specific consequence. */
  confirmationText?: Partial<Record<JobArchiveOperation, string>>;
}

const terminalJobStatuses = new Set<string>([
  JobStatus.Completed,
  JobStatus.Failed,
  JobStatus.FailedToStart,
  JobStatus.Cancelled,
]);

const operationConfig = {
  archive: {
    label: 'Archive job',
    Icon: ArchiveOutlinedIcon,
  },
  restore: {
    label: 'Restore job',
    Icon: UnarchiveOutlinedIcon,
  },
} as const;

export const useJobArchiveAction = (job: JobArchiveControlProps['job']) => {
  const currentUserId = useAppSelector((state) => state.auth.profile?.userId);
  const { hasPermission, isLoading: isLoadingPermissions } = usePermissions();
  const canManageAllJobs = hasPermission(Permission.ArchiveRestoreAllUsersJobs);
  const canManageOwnJob =
    hasPermission(Permission.ArchiveRestoreOwnJobs) && Boolean(currentUserId) && job.userId === currentUserId;
  const isTerminal = Boolean(job.status && terminalJobStatuses.has(job.status));
  const pendingOperation = usePendingJobArchiveOperation(job.id);
  const operation: JobArchiveOperation = pendingOperation || (job.deletedAt ? 'restore' : 'archive');

  return {
    isAvailable: !isLoadingPermissions && isTerminal && (canManageAllJobs || canManageOwnJob),
    operation,
    ...operationConfig[operation],
    isPending: Boolean(pendingOperation),
  };
};

export const JobArchiveControl: React.FC<JobArchiveControlProps> = ({
  job,
  labeled = false,
  contained = false,
  confirmationNotes,
  confirmationText,
  actionLabels,
  onArchived,
}) => {
  const confirm = useConfirmation();
  const archiveMutation = useJobArchiveMutation();
  const { isAvailable, operation, Icon, label, isPending } = useJobArchiveAction(job);
  const actionLabel = actionLabels?.[operation] || label;

  if (!isAvailable) {
    return null;
  }

  const requestOperation = async () => {
    if (archiveMutation.isPendingForJob(job.id)) {
      return;
    }

    const displayJob = job.name || job.id;
    const defaultConfirmationText =
      operation === 'archive'
        ? `Archive ${displayJob}? The job will leave the Jobs view. Its history and directly produced results remain recoverable.`
        : `Restore ${displayJob}? The job will return to the Jobs view with eligible directly produced results.`;
    const confirmed = await confirm({
      title: `${actionLabel}?`,
      text:
        confirmationText?.[operation] || compact([defaultConfirmationText, confirmationNotes?.[operation]]).join(' '),
      confirmButtonProps: {
        title: actionLabels?.[operation] || operationConfig[operation].label.replace(' job', ''),
        color: contained ? 'secondary' : operation === 'archive' ? 'warning' : 'primary',
      },
    });

    if (confirmed && !archiveMutation.isPendingForJob(job.id)) {
      archiveMutation.mutate(
        { jobId: job.id, operation },
        {
          onSuccess: () => {
            if (operation === 'archive') {
              onArchived?.();
            }
          },
        }
      );
    }
  };

  if (labeled) {
    return (
      <Button
        size="small"
        variant={contained ? 'contained' : 'outlined'}
        color={contained ? 'secondary' : operation === 'archive' ? 'warning' : 'primary'}
        startIcon={isPending ? <CircularProgress size={16} /> : <Icon />}
        disabled={isPending}
        onClick={(event) => {
          event.stopPropagation();
          void requestOperation();
        }}
        sx={{ textTransform: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}
      >
        {isPending ? `${operation === 'archive' ? 'Archiving' : 'Restoring'}...` : actionLabel}
      </Button>
    );
  }

  return (
    <Tooltip title={actionLabel}>
      <span>
        <IconButton
          size="small"
          aria-label={actionLabel}
          disabled={isPending}
          onClick={(event) => {
            event.stopPropagation();
            void requestOperation();
          }}
        >
          {isPending ? <CircularProgress size={20} /> : <Icon />}
        </IconButton>
      </span>
    </Tooltip>
  );
};
